import React, { useState } from 'react';
import './DatabaseManager.css';

function DatabaseManager({ databases, onRefresh }) {
  const [newDbName, setNewDbName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreateDatabase = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newDbName.trim()) {
      setError('Database name is required');
      return;
    }

    if (!newDbName.endsWith('.db')) {
      setError('Database name must end with .db');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/databases/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newDbName })
      });

      if (response.ok) {
        setSuccess(`Database "${newDbName}" created successfully`);
        setNewDbName('');
        onRefresh();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create database');
      }
    } catch (err) {
      setError('Error creating database: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDatabase = async (dbName) => {
    if (window.confirm(`Are you sure you want to delete "${dbName}"? This cannot be undone.`)) {
      try {
        const response = await fetch(`/api/databases/${dbName}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          setSuccess(`Database "${dbName}" deleted successfully`);
          onRefresh();
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to delete database');
        }
      } catch (err) {
        setError('Error deleting database: ' + err.message);
      }
    }
  };

  const handleDownloadDatabase = (dbName) => {
    // In a real app, this would download the database file
    const link = document.createElement('a');
    link.href = `/database/${dbName}`;
    link.download = dbName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const databaseList = Array.isArray(databases) ? databases : [];

  return (
    <div className="database-manager-page">
      <div className="manager-container">
        <h1>Database Management</h1>

        <div className="create-db-section">
          <h2>Create New Database</h2>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleCreateDatabase} className="create-db-form">
            <div className="form-group">
              <input
                type="text"
                value={newDbName}
                onChange={(e) => setNewDbName(e.target.value)}
                placeholder="Enter database name (e.g., my_storage.db)"
                className="form-input"
                disabled={loading}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Database'}
            </button>
          </form>
        </div>

        <div className="databases-section">
          <h2>Existing Databases ({databaseList.length})</h2>
          
          {databaseList.length === 0 ? (
            <p className="no-databases">No databases found. Create one to get started!</p>
          ) : (
            <div className="databases-list">
              {databaseList.map(db => (
                <div key={db.name} className="database-card">
                  <div className="db-info">
                    <h3>{db.name}</h3>
                    <p className="db-path">{db.path}</p>
                  </div>
                  <div className="db-actions">
                    <button
                      onClick={() => handleDownloadDatabase(db.name)}
                      className="btn btn-small btn-secondary"
                      title="Download database for backup"
                    >
                      ⬇ Download
                    </button>
                    <button
                      onClick={() => handleDeleteDatabase(db.name)}
                      className="btn btn-small btn-danger"
                      title="Delete this database"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="info-section">
          <h3>Database Information</h3>
          <ul className="info-list">
            <li>Databases are stored as SQLite files with a .db extension</li>
            <li>You can download a database to share it with others</li>
            <li>Each database contains all locations and items for that database</li>
            <li>Deleting a database permanently removes all its data</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default DatabaseManager;
